import { useRef, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Send,
    Image as ImageIcon,
    Paperclip,
    Loader2,
    CheckCircle,
} from 'lucide-react'

interface ChatInputProps {
    value: string
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
    onSend: () => void
    onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
    isConnected: boolean
    isSending: boolean
    isUploading: boolean
    isDone: boolean
}

export interface ChatInputHandle {
    focus: () => void
    triggerImageClick: () => void
    triggerFileClick: () => void
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
    (
        {
            value,
            onChange,
            onKeyDown,
            onSend,
            onImageSelect,
            onFileSelect,
            isConnected,
            isSending,
            isUploading,
            isDone,
        },
        ref
    ) => {
        const { t } = useTranslation()
        const inputRef = useRef<HTMLTextAreaElement>(null)
        const imageInputRef = useRef<HTMLInputElement>(null)
        const fileInputRef = useRef<HTMLInputElement>(null)

        useImperativeHandle(ref, () => ({
            focus: () => inputRef.current?.focus(),
            triggerImageClick: () => imageInputRef.current?.click(),
            triggerFileClick: () => fileInputRef.current?.click(),
        }))

        // Completed state
        if (isDone) {
            return (
                <div className="p-4 border-t bg-gray-50">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                        <CheckCircle size={16} className="text-green-500" />
                        <p className="text-sm">
                            {t(
                                'This conversation has been marked as completed.'
                            )}
                        </p>
                    </div>
                </div>
            )
        }

        const isDisabled = !isConnected || isSending || isUploading
        const canSend = value.trim() && !isDisabled

        return (
            <div className="p-3 border-t bg-white">
                {/* Hidden file inputs */}
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onImageSelect}
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="*/*"
                    className="hidden"
                    onChange={onFileSelect}
                />

                {/* Upload progress indicator */}
                {isUploading && (
                    <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-blue-50 rounded-lg">
                        <Loader2
                            size={16}
                            className="animate-spin text-blue-500"
                        />
                        <span className="text-xs text-blue-600">
                            {t('Uploading file...')}
                        </span>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* Image picker button */}
                    <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={isDisabled}
                        className={`flex-shrink-0 w-[44px] h-[44px] flex items-center justify-center rounded-full transition-colors ${
                            isDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        title={t('Send image')}
                    >
                        <ImageIcon size={20} />
                    </button>

                    {/* File picker button */}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isDisabled}
                        className={`flex-shrink-0 w-[44px] h-[44px] flex items-center justify-center rounded-full transition-colors ${
                            isDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        title={t('Attach file')}
                    >
                        <Paperclip size={20} />
                    </button>

                    <textarea
                        ref={inputRef}
                        value={value}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        placeholder={t('Type a message...')}
                        className="flex-1 resize-none border border-gray-200 rounded-2xl px-4 py-3 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 h-[44px] max-h-[120px] text-sm leading-[18px]"
                        rows={1}
                        disabled={isDisabled}
                    />

                    <button
                        type="button"
                        onClick={onSend}
                        disabled={!canSend}
                        className={`flex-shrink-0 w-[44px] h-[44px] flex items-center justify-center rounded-full transition-all ${
                            !canSend
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                        }`}
                    >
                        <Send
                            size={18}
                            className={isSending ? 'animate-pulse' : ''}
                        />
                    </button>
                </div>

                {!isConnected && (
                    <p className="text-xs text-red-500 mt-2 text-center">
                        {t('Not connected. Please wait or refresh the page.')}
                    </p>
                )}
            </div>
        )
    }
)

ChatInput.displayName = 'ChatInput'

export default ChatInput
